import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Icon,
  IconButton,
  Spinner,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from '@chakra-ui/react';
import { CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  LuFile,
  LuRotateCw,
  LuClock,
  LuCircleAlert,
  LuEllipsis,
  LuArrowUpRight,
  LuScanSearch,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import i18n from '../../i18n';
import { useFilters } from '../../contexts/FilterContext';
import { DATE_MODES } from '../../utils/dateUtils';
import { getStepResults, getProcessStatus } from '../../api/endpointsBackend';
import { formatRelativeTime, formatFileSize, processErrorText } from '../../utils/statementHelpers';
import MovementsList from '../shared/MovementsList';
import MovementDetailPanel from '../movements/MovementDetailPanel';
import SourceFileProcessingPipeline from './SourceFileProcessingPipeline';
import StatementReviewCard from '../repairs/checkup/StatementReviewCard';
import { CHECKUP_PATH } from '../repairs/checkup/CheckupBanner';
import { fileMeta, StatusPill, TINT } from './sourceFilesUI';

const formatUploadDate = (dateStr) => {
  const date = new Date(dateStr);
  const months = i18n.t('months.short', { ns: 'dates', returnObjects: true });
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
};

function StateBanner({ status, process }) {
  const { t } = useTranslation('sourceFiles');
  if (status === 'running' || status === 'pending') {
    return (
      <HStack
        spacing={3}
        p={3}
        borderRadius="md"
        {...TINT.blue}
        align="flex-start"
      >
        <Icon as={LuClock} boxSize="18px" color="redesign.accent.blue" mt="2px" />
        <Box>
          <Text fontSize="sm" fontWeight={600} color="redesign.accent.blue">
            {t(status === 'pending' ? 'detail.waiting' : 'detail.processing')}
          </Text>
          <Text fontSize="xs" color="redesign.text.muted">
            {t(status === 'pending' ? 'detail.waitingHint' : 'detail.processingHint')}
          </Text>
        </Box>
      </HStack>
    );
  }
  if (status === 'failed') {
    const msg =
      processErrorText(process, t) ||
      t('detail.processingFailedHint');
    return (
      <HStack
        spacing={3}
        p={3}
        borderRadius="md"
        {...TINT.red}
        align="flex-start"
      >
        <Icon as={LuCircleAlert} boxSize="18px" color="redesign.accent.red" mt="2px" />
        <Box>
          <Text fontSize="sm" fontWeight={600} color="redesign.accent.red">
            {t('detail.processingFailed')}
          </Text>
          <Text fontSize="xs" color="redesign.text.muted" noOfLines={3}>
            {msg}
          </Text>
        </Box>
      </HStack>
    );
  }
  return null;
}

export default function SourceFileDetailPanel({
  process,
  onClose,
  onDeleteRequest,
  onRetry,
  onCancel,
  categories,
  allTags,
  repairFlags,
}) {
  const { t } = useTranslation('sourceFiles');
  const navigate = useNavigate();
  const { clearFilters, setDateMode, addFilter } = useFilters();
  const [stepsDetails, setStepsDetails] = useState(null);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [livePending, setLivePending] = useState(null);
  const [selectedMovementId, setSelectedMovementId] = useState(null);
  const [movementsRefreshKey, setMovementsRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const liveProcess = livePending || process;
  const sourceFile = liveProcess?.source_file;
  const sourceFileId = sourceFile?.id;
  const status = liveProcess?.status;
  const fileType = sourceFile?.file_type;
  const meta = fileMeta(fileType);
  // A statement the checkup can judge (its import kept the rows it read,
  // and its page text or its balances can judge them) can be checked from
  // here, whether or not it needs a look.
  const canCheck = Boolean(repairFlags?.checkable && sourceFileId);

  const movementsFilters = useMemo(
    () =>
      sourceFileId
        ? [{ entity: 'SourceFile', column: 'id', operator: 'in', values: [sourceFileId] }]
        : null,
    [sourceFileId]
  );

  // Open the Movements page pre-filtered to this file. Set the filter state
  // directly (not just the URL) — FilterContext re-serializes its state to the
  // URL on route change, so a raw URL would be overwritten. mode=ALL so a
  // statement for an old period still shows (filter options are date-scoped).
  const openInMovements = useCallback(() => {
    if (!sourceFileId) return;
    clearFilters();
    setDateMode(DATE_MODES.ALL);
    addFilter({ entity: 'SourceFile', column: 'id', operator: 'in', values: [sourceFileId] });
    navigate('/movements');
  }, [sourceFileId, clearFilters, setDateMode, addFilter, navigate]);

  const movementsCreated = useMemo(() => {
    const step5 = stepsDetails?.find((s) => s.step_number === 5);
    return step5?.results?.movement?.created?.count ?? null;
  }, [stepsDetails]);

  const fetchSteps = useCallback(async (processId) => {
    if (!processId) return;
    setLoadingSteps(true);
    try {
      const stepPromises = [1, 2, 3, 4, 5].map(async (stepNum) => {
        try {
          const result = await getStepResults(processId, stepNum);
          return { step_number: stepNum, ...result };
        } catch {
          return null;
        }
      });
      const results = (await Promise.all(stepPromises)).filter(Boolean);
      setStepsDetails(results);
    } finally {
      setLoadingSteps(false);
    }
  }, []);

  useEffect(() => {
    setStepsDetails(null);
    setLivePending(null);
    setSelectedMovementId(null);
    if (process?.process_id) {
      fetchSteps(process.process_id);
    }
  }, [process?.process_id, fetchSteps]);

  // Poll while active to update steps and status in place
  useEffect(() => {
    if (!process?.process_id) return;
    const isActive = status === 'running' || status === 'pending';
    if (!isActive) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    const tick = async () => {
      try {
        const fresh = await getProcessStatus(process.process_id);
        setLivePending((prev) => ({ ...(prev || process), ...fresh }));
        await fetchSteps(process.process_id);
        if (['completed', 'failed', 'cancelled'].includes(fresh.status)) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          if (fresh.status === 'completed') {
            setMovementsRefreshKey((k) => k + 1);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    pollRef.current = setInterval(tick, 2500);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [process?.process_id, status, process, fetchSteps]);

  if (!process) {
    return (
      <Flex direction="column" align="center" justify="center" gap={3} h="100%" color="redesign.text.faint">
        <Icon as={LuFile} boxSize="32px" />
        <Text fontSize="sm" color="redesign.text.muted">
          {t('detail.selectFile')}
        </Text>
      </Flex>
    );
  }

  return (
    <Box h="100%" display="flex" flexDirection="column" bg="redesign.bg.surface" position="relative">
      {selectedMovementId && (
        <Box position="absolute" inset={0} bg="redesign.bg.surface" zIndex={2} display="flex" flexDirection="column">
          <MovementDetailPanel
            movementId={selectedMovementId}
            onClose={() => {
              setSelectedMovementId(null);
              setMovementsRefreshKey((k) => k + 1);
            }}
            onMovementUpdated={() => {}}
            onMovementDeleted={() => {}}
            onSelectMovement={setSelectedMovementId}
          />
        </Box>
      )}

      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        px={5}
        py={3}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        <Text fontSize="sm" fontWeight={500} color="redesign.text">
          {t('detail.fileDetails')}
        </Text>
        <HStack spacing={1}>
          {(onDeleteRequest || canCheck) && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Icon as={LuEllipsis} boxSize="16px" />}
                variant="ghost"
                size="sm"
                aria-label={t('detail.options')}
                color="redesign.text.muted"
              />
              <MenuList>
                {canCheck && (
                  <MenuItem
                    icon={<Icon as={LuScanSearch} boxSize="14px" />}
                    onClick={() => navigate(`${CHECKUP_PATH}?statement=${sourceFileId}`)}
                  >
                    {t('detail.checkStatement')}
                  </MenuItem>
                )}
                {onDeleteRequest && (
                  <MenuItem
                    icon={<DeleteIcon />}
                    color="redesign.accent.red"
                    onClick={() => sourceFile && onDeleteRequest(sourceFile)}
                    isDisabled={!sourceFile}
                  >
                    {t('detail.delete')}
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
          <IconButton
            icon={<CloseIcon boxSize={2} />}
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label={t('detail.closeAria')}
            color="redesign.text.muted"
          />
        </HStack>
      </Flex>

      {/* Body */}
      <Box flex={1} overflowY="auto" overflowX="hidden" px={5} py={5}>
        {/* Filename row */}
        <Flex align="center" gap={3} mb={5}>
          <Flex w="40px" h="40px" borderRadius="lg" bg={meta.bg} _dark={meta._dark} align="center" justify="center" flexShrink={0}>
            <Icon as={meta.icon} boxSize="20px" color={meta.color} />
          </Flex>
          <Box flex={1} minW={0}>
            <Tooltip label={sourceFile?.filename}>
              <Text fontSize="md" fontWeight={600} color="redesign.heading" noOfLines={1}>
                {sourceFile?.filename || t('list.unknownFile')}
              </Text>
            </Tooltip>
            {sourceFile?.file_size && (
              <Text fontSize="xs" color="redesign.text.muted">
                {formatFileSize(sourceFile.file_size)}
              </Text>
            )}
          </Box>
          <StatusPill status={status} />
        </Flex>

        {/* Metadata card */}
        <VStack spacing={3} align="stretch" p={4} bg="redesign.bg.card" borderRadius="xl" mb={5}>
          <Flex justify="space-between">
            <Text fontSize="xs" color="redesign.text.muted">{t('detail.uploaded')}</Text>
            <Text fontSize="xs" color="redesign.text">
              {sourceFile?.upload_date
                ? `${formatUploadDate(sourceFile.upload_date)} · ${formatRelativeTime(sourceFile.upload_date)}`
                : '—'}
            </Text>
          </Flex>
          {movementsCreated != null && (
            <Flex justify="space-between">
              <Text fontSize="xs" color="redesign.text.muted">{t('detail.movements')}</Text>
              <Text fontSize="xs" color="redesign.text">
                {movementsCreated} {t('detail.extracted')}
              </Text>
            </Flex>
          )}
        </VStack>

        {/* State banner */}
        <Box mb={5}>
          <StateBanner
            status={status}
            process={liveProcess}
          />
        </Box>

        {/* The checkup: what this statement's flags mean, and a way in */}
        {repairFlags && sourceFileId && (
          <Box mb={5} p={4} bg="redesign.bg.card" borderRadius="xl">
            <StatementReviewCard sourceFileId={sourceFileId} flags={repairFlags} />
          </Box>
        )}

        {/* Pipeline */}
        <Box mb={5}>
          {loadingSteps && !stepsDetails ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" />
            </Flex>
          ) : (
            <SourceFileProcessingPipeline
              process={liveProcess}
              stepsDetails={stepsDetails}
              onRetry={onRetry}
              onCancel={onCancel}
            />
          )}
        </Box>

        {/* Movements created */}
        {status === 'completed' && sourceFileId && (
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" fontWeight={600} color="redesign.heading">
                {t('detail.movementsCreated')}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                color="redesign.accent.blue"
                rightIcon={<Icon as={LuArrowUpRight} boxSize="14px" />}
                onClick={openInMovements}
              >
                {t('detail.openInMovements')}
              </Button>
            </Flex>
            <MovementsList
              filters={movementsFilters}
              dateRange={null}
              currency="all"
              categories={categories}
              allTags={allTags}
              onMovementClick={setSelectedMovementId}
              refreshKey={movementsRefreshKey}
              showAccountName
              emptyLabel={t('detail.thisFile')}
            />
          </Box>
        )}

        {/* Footer actions */}
        {status === 'failed' && onRetry && (
          <Flex gap={2} mt={6}>
            <Button
              size="sm"
              leftIcon={<Icon as={LuRotateCw} />}
              variant="outline"
              onClick={() => onRetry(liveProcess.process_id)}
            >
              {t('detail.reprocess')}
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
}
